import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { DogSelector } from "./DogSelector";
import { User, Dog } from "../../types";
import { CanicrossFormData } from "../../utils/trail/trailPayloads";
import { MapSection } from "./MapSection";
import { GpxSection } from "./GpxSection";
import { parseGPXFile } from "../../utils/gpxParser";
import { parseTCXFile, summarizeTCXSamples } from "../../utils/tcxParser";

export interface CanicrossFormHandle {
  getData: () => CanicrossFormData;
  setData: (data: Partial<CanicrossFormData>) => void;
}

interface CanicrossFormProps {
  user: User | null;
  dogsPlaceholder?: Dog[];
  location: string;
  setLocation: (location: string) => void;
  onDistanceChange: (distance: number) => void;
  onDurationChange: (duration: number) => void;
  onLocationCoordinateChange: (coords: [number, number]) => void;
  onDateChange?: (date: string) => void;
}

const defaultCoordinates: [number, number] = [46.2563, 5.6554];

export const CanicrossForm = forwardRef<CanicrossFormHandle, CanicrossFormProps>(
  ({ dogsPlaceholder, location, setLocation, onLocationCoordinateChange, onDateChange, onDistanceChange, onDurationChange }, ref) => {
    const [selectedDogId, setSelectedDogId] = useState("");
    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
    const [distance, setDistance] = useState(0);
    const [duration, setDuration] = useState(0);
    const [avgSpeed, setAvgSpeed] = useState(0);
    const [maxSpeed, setMaxSpeed] = useState(0);
    const [avgPace, setAvgPace] = useState(0);
    const [maxPace, setMaxPace] = useState<number | undefined>();
    const [avgHeartRate, setAvgHeartRate] = useState<number | undefined>();
    const [maxHeartRate, setMaxHeartRate] = useState<number | undefined>();
    const [positiveElevation, setPositiveElevation] = useState(0);
    const [negativeElevation, setNegativeElevation] = useState(0);
    const [terrain, setTerrain] = useState("");
    const [pauseTime, setPauseTime] = useState(0);
    const [energyLevel, setEnergyLevel] = useState(0);
    const [notes, setNotes] = useState("");
    const [locationCoordinate, setLocationCoordinateState] = useState(defaultCoordinates);
    const [gpxFile, setGpxFile] = useState<File | null>(null);
    const [gpxData, setGpxData] = useState<any>(null);
    const [previewUserPath, setPreviewUserPath] = useState<[number, number][] | null>(null);
    const [gpxError, setGpxError] = useState("");
    const setLocationCoordinate = (coordinates: [number, number]) => {
      setLocationCoordinateState(coordinates);
      onLocationCoordinateChange(coordinates);
    };

    useImperativeHandle(ref, () => ({
      getData: () => ({
        selectedDogId,
        date,
        distance,
        duration,
        avgSpeed,
        maxSpeed,
        avgPace,
        maxPace,
        avgHeartRate,
        maxHeartRate,
        positiveElevation,
        negativeElevation,
        terrain,
        pauseTime,
        energyLevel,
        notes,
        location: "",
        locationCoordinate,
        gpxData,
      }),
      setData: (data) => {
        if (data.selectedDogId !== undefined) setSelectedDogId(data.selectedDogId);
        if (data.date !== undefined) setDate(data.date);
        if (data.distance !== undefined) setDistance(data.distance);
        if (data.duration !== undefined) setDuration(data.duration);
        if (data.avgSpeed !== undefined) setAvgSpeed(data.avgSpeed);
        if (data.maxSpeed !== undefined) setMaxSpeed(data.maxSpeed);
        if (data.avgPace !== undefined) setAvgPace(data.avgPace);
        if ("maxPace" in data) setMaxPace(data.maxPace);
        if ("avgHeartRate" in data) setAvgHeartRate(data.avgHeartRate);
        if ("maxHeartRate" in data) setMaxHeartRate(data.maxHeartRate);
        if (data.positiveElevation !== undefined) setPositiveElevation(data.positiveElevation);
        if (data.negativeElevation !== undefined) setNegativeElevation(data.negativeElevation);
        if (data.terrain !== undefined) setTerrain(data.terrain);
        if (data.pauseTime !== undefined) setPauseTime(data.pauseTime);
        if (data.energyLevel !== undefined) setEnergyLevel(data.energyLevel);
        if (data.notes !== undefined) setNotes(data.notes);
        if (data.locationCoordinate !== undefined) setLocationCoordinate(data.locationCoordinate);
        if (data.gpxData !== undefined) setGpxData(data.gpxData);
      },
    }));

    const handleGPXUpload = () => async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const extension = file.name.toLowerCase().split(".").pop();
      if (extension !== "gpx" && extension !== "tcx") {
        setGpxError("Veuillez sélectionner un fichier GPX ou TCX valide.");
        return;
      }
      try {
        const content = await file.text();
        const parsed = extension === "tcx" ? parseTCXFile(content) : parseGPXFile(content);
        if (!parsed) {
          setGpxError(`Impossible de lire cette trace ${extension.toUpperCase()}.`);
          return;
        }
        setGpxFile(file);
        setGpxData(parsed);
        setPreviewUserPath(parsed.path);
        setDistance(parsed.distance || 0);
        setDuration(parsed.duration || 0);
        setPositiveElevation(parsed.elevationGain || 0);
        if (parsed.date) {
          const activityDate = parsed.date.slice(0, 10);
          setDate(activityDate);
          onDateChange?.(activityDate);
        }
        setNegativeElevation(parsed.elevationLoss || 0);
        setAvgSpeed(parsed.avgSpeed || 0);
        setMaxSpeed(parsed.maxSpeed || 0);
        setAvgPace(parsed.avgPace || 0);
        setMaxPace(parsed.maxPace);
        setAvgHeartRate(parsed.avgHeartRate);
        setMaxHeartRate(parsed.maxHeartRate);
        onDistanceChange(parsed.distance);
        if (parsed.duration !== undefined) onDurationChange(parsed.duration);
        if (parsed.center) setLocationCoordinate(parsed.center);
        setGpxError("");
      } catch {
        setGpxError(`Impossible de lire cette trace ${extension.toUpperCase()}.`);
      }
    };

    const handleRemoveGPX = () => () => {
      setGpxFile(null);
      setGpxData(null);
      setPreviewUserPath(null);
    };

    const handlePreviewUserPath = (path: [number, number][]) => {
      setPreviewUserPath(path);
    };

    const updateUserPath = (
      path: [number, number][],
      timestamps?: (string | Date)[],
      range?: { startIndex: number; endIndex: number },
    ) => {
      if (!gpxData) return;
      if (!gpxData.samples) {
        setGpxData({ ...gpxData, path, timestamps });
          setPreviewUserPath(path);
        return;
      }
      const samples = range
        ? gpxData.samples.slice(range.startIndex, range.endIndex + 1)
        : gpxData.samples;
      const metrics = summarizeTCXSamples(samples);
      setDistance(metrics.distance);
      setDuration(metrics.duration || 0);
      setPositiveElevation(metrics.elevationGain);
      setNegativeElevation(metrics.elevationLoss);
      setAvgSpeed(metrics.avgSpeed || 0);
      setMaxSpeed(metrics.maxSpeed || 0);
      setAvgPace(metrics.avgPace || 0);
      setMaxPace(metrics.maxPace);
      setAvgHeartRate(metrics.avgHeartRate);
      setMaxHeartRate(metrics.maxHeartRate);
      onDistanceChange(metrics.distance);
      if (metrics.duration !== undefined) onDurationChange(metrics.duration);
      setGpxData({
        ...gpxData,
        ...metrics,
        path,
        timestamps,
        samples,
        center: [
          path.reduce((sum, point) => sum + point[0], 0) / path.length,
          path.reduce((sum, point) => sum + point[1], 0) / path.length,
        ],
        startPoint: path[0],
        endPoint: path[path.length - 1],
      });
      setPreviewUserPath(path);
    };

    const numericSetter = (setter: (value: number) => void) =>
      (event: React.ChangeEvent<HTMLInputElement>) => setter(Number(event.target.value) || 0);

    return (
      <div className="space-y-4">
        <DogSelector
          value={selectedDogId}
          onChange={setSelectedDogId}
          required
          dogPlaceHolder={dogsPlaceholder}
        />

        <GpxSection
          category="canicross"
          dogGpxFile={null}
          userGpxFile={gpxFile}
          dogGpxData={null}
          userGpxData={gpxData}
          gpxError={gpxError}
          handleGPXUpload={handleGPXUpload}
          handleRemoveGPX={handleRemoveGPX}
          handleUpdateDogPath={() => undefined}
          handlePreviewDogPath={() => undefined}
          handleUpdateUserPath={updateUserPath}
          handlePreviewUserPath={handlePreviewUserPath}
        />

        <MapSection
          category="canicross"
          dogGpxData={null}
          userGpxData={gpxData}
          locationCoordinate={locationCoordinate}
          previewDogPath={null}
          previewUserPath={previewUserPath || gpxData?.path || null}
          setLocation={setLocation}
          setLocationCoordinate={setLocationCoordinate}
          location={location}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="canicross-distance">Distance (m)</Label>
            <Input id="canicross-distance" type="number" min="0" value={distance} onChange={(event) => { const value = Number(event.target.value) || 0; setDistance(value); }} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-duration">Durée (secondes)</Label>
            <Input id="canicross-duration" type="number" min="0" value={duration} onChange={(event) => setDuration(Number(event.target.value) || 0)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-avg-speed">Vitesse moyenne (km/h)</Label>
            <Input id="canicross-avg-speed" type="number" min="0" step="0.1" value={avgSpeed} onChange={numericSetter(setAvgSpeed)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-max-speed">Vitesse maximale (km/h)</Label>
            <Input id="canicross-max-speed" type="number" min="0" step="0.1" value={maxSpeed} onChange={numericSetter(setMaxSpeed)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-avg-heart-rate">Fréquence cardiaque moyenne (bpm)</Label>
            <Input id="canicross-avg-heart-rate" type="number" min="0" value={avgHeartRate ?? ""} onChange={(event) => setAvgHeartRate(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-max-heart-rate">Fréquence cardiaque maximale (bpm)</Label>
            <Input id="canicross-max-heart-rate" type="number" min="0" value={maxHeartRate ?? ""} onChange={(event) => setMaxHeartRate(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-avg-pace">Allure moyenne (min/km)</Label>
            <Input id="canicross-avg-pace" type="number" min="0" step="0.1" value={avgPace} onChange={numericSetter(setAvgPace)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-max-pace">Allure maximale (meilleure, min/km)</Label>
            <Input id="canicross-max-pace" type="number" min="0" step="0.1" value={maxPace ?? ""} onChange={(event) => setMaxPace(event.target.value === "" ? undefined : Number(event.target.value))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-pause-time">Temps de pause (secondes)</Label>
            <Input id="canicross-pause-time" type="number" min="0" value={pauseTime} onChange={numericSetter(setPauseTime)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-positive-elevation">Dénivelé positif (m)</Label>
            <Input id="canicross-positive-elevation" type="number" min="0" value={positiveElevation} onChange={numericSetter(setPositiveElevation)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="canicross-negative-elevation">Dénivelé négatif (m)</Label>
            <Input id="canicross-negative-elevation" type="number" min="0" value={negativeElevation} onChange={numericSetter(setNegativeElevation)} />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="canicross-terrain">Terrain</Label>
          <Input id="canicross-terrain" value={terrain} onChange={(event) => setTerrain(event.target.value)} placeholder="Ex: sentier, route, forêt" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="canicross-energy">Niveau d'énergie (0 à 10)</Label>
          <Input id="canicross-energy" type="number" min="0" max="10" value={energyLevel} onChange={numericSetter(setEnergyLevel)} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="canicross-notes">Notes</Label>
          <Textarea id="canicross-notes" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Observations sur la sortie..." />
        </div>
      </div>
    );
  },
);

CanicrossForm.displayName = "CanicrossForm";
